import Box from '@material-ui/core/Box';
import { SxProps } from '@material-ui/system';
import { findIndex } from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

type ColumnProps = {
  xs?: SxProps;
  title?: boolean;
  selected?: boolean;
};

const Row = styled(({ sx, isPrint, ...rest }) => (
  <Box
    sx={{
      display: 'grid',
      columnGap: 0.25,
      gridTemplateColumns: isPrint ? 'repeat(24, 33px)' : 'repeat(24, 50px)',
      '@media print': { gridTemplateColumns: 'repeat(24, 30px)' },
      ...sx,
    }}
    {...rest}
  />
))``;

const Column = styled(({ sx, isPrint, ...rest }) => (
  <Box
    sx={{
      border: ' 0.5px solid rgba(33, 33, 33, 0.23)',
      height: 24,
      position: 'relative',
      ...sx,
    }}
    {...rest}
  />
))<ColumnProps>`
  background: ${({ disabled }) => (disabled ? `#ebedf3` : `#f3f6f9`)};
  color: #68686a;
  cursor: ${({ disabled }) => (disabled ? `default` : `pointer`)};
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;

  ${({ title, selected, isPrint, disabled }) => css`
    ${title &&
    `
      background: ${disabled ? '#f3f6f9' : '#fff'};
      border-radius: 2px;
      cursor: auto;
    `};

    ${!title &&
    selected &&
    `
      &::after {
        background: #5CABDD;
        content: ' ';
        border-radius: 2px;
        width: ${isPrint ? '10px' : '20px'};
        height: 20px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    `};
  `}

  ${({ title }) => title && css``}
`;

export type TimeProps = {
  hour: number;
  first: boolean;
  last: boolean;
};
export type RowDataProps = {
  key: string;
  title: string;
  times: TimeProps[];
};
export type RowTitleProps = {
  rows: RowDataProps[];
  unfocus: Array<string>;
  isPrint?: boolean;
};
export type RowHourClickProps = {
  activityIndex: number;
  key: string;
  hour: number;
  type: 'first' | 'last';
};

export type RowHourProps = {
  hours: Array<number>;
  rows: RowDataProps[];
  handleClick: (data: RowHourClickProps) => void;
  disabled?: boolean;
  isPrint?: boolean;
};

export const RowTitle: React.VFC<RowTitleProps> = ({ rows, unfocus, isPrint }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: 'auto',
        display: 'grid',
        rowGap: 0.25,
        gridAutoRows: '24px',
      }}
    >
      <div
        style={{
          fontStyle: isPrint ? 'normal' : 'italic',
          fontSize: isPrint ? '14px' : 'inherit',
          color: isPrint ? 'black' : 'inherit',
        }}
      >
        {t('time_of_day')}
      </div>
      {rows.map((row) => (
        <Column
          key={row.title}
          sx={{
            border: 0,
            background: 'transparent',
            color: !unfocus.find((data) => data === row.key)
              ? isPrint
                ? 'black'
                : '#68686A'
              : '#B5B5C3',
            fontSize: isPrint ? '9pt' : 'inherit',
          }}
        >
          {row.title}
        </Column>
      ))}
    </Box>
  );
};

export const RowHour: React.VFC<RowHourProps> = ({
  hours,
  rows,
  handleClick,
  disabled,
  isPrint,
}) => {
  const handleIsSelected = useCallback(
    (times: TimeProps[], hour: number, type: 'first' | 'last') => {
      return (
        findIndex(times, {
          hour: hour,
          [type]: true,
        }) !== -1
      );
    },
    [],
  );

  return (
    <Box
      sx={{
        overflowX: 'scroll',
        display: 'grid',
        rowGap: 0.25,
        pr: 0.25,
      }}
    >
      <Row isPrint={isPrint}>
        {hours.map((hour) => (
          <Column
            disabled={disabled}
            key={hour}
            sx={{
              textAlign: 'center',
              padding: '0 0.7px',
            }}
          >
            {hour}
          </Column>
        ))}
      </Row>

      {rows.map((row, i) => (
        <Row key={row.key} isPrint={isPrint}>
          {hours.map((hour) => (
            <Box
              key={hour}
              sx={{
                display: 'grid',
                columnGap: 0.25,
                gridTemplateColumns: '1fr 1fr',
              }}
            >
              <Column
                disabled={disabled}
                isPrint={isPrint}
                selected={handleIsSelected(row.times, hour, 'first')}
                onClick={() => {
                  handleClick({
                    activityIndex: i,
                    key: row.key,
                    hour,
                    type: 'first',
                  });
                }}
              />
              <Column
                disabled={disabled}
                isPrint={isPrint}
                selected={handleIsSelected(row.times, hour, 'last')}
                onClick={() => {
                  handleClick({
                    activityIndex: i,
                    key: row.key,
                    hour,
                    type: 'last',
                  });
                }}
              />
            </Box>
          ))}
        </Row>
      ))}
    </Box>
  );
};
